import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { useTranslations } from "next-intl";
import { ImageOff, ImagePlus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { NoImageAvailable } from "@/components/atoms/NoImageAvailable";
import { cn } from "@/lib/utils";
import HeroImageGrid, { type HeroImageGridVariant } from "./HeroImageGrid";
type Props = {
  pictures: string[];
  title?: string;
  subtitle?: string;
  variant?: HeroImageGridVariant;
  progressive?: boolean;
  onImagesClick?: () => void;
  onTileClick?: (index: number) => void;
  onAdd?: () => void;
  addLabel?: string;
  morePhotosLabel?: (count: number) => ReactNode;
  altPrefix?: string;
  imagePriority?: boolean;
  imageQuality?: number;
  imageSizes?: {
    primary?: string;
    secondary?: string;
  };
  className?: string;
  contentClassName?: string;
  /** Render flat (no Card chrome), e.g. the detail page layout. */
  bare?: boolean;
  /** Stretch the image grid to fill its container height (drops the fixed
   *  aspect ratio) so it can match a sibling column's height. */
  fill?: boolean;
  children?: ReactNode;
};
export function HeroGridCard(t0) {
  const $ = _c(48);
  const {
    pictures,
    title,
    subtitle,
    variant: t1,
    progressive: t2,
    onImagesClick,
    onTileClick,
    onAdd,
    addLabel,
    morePhotosLabel,
    altPrefix,
    imagePriority,
    imageQuality,
    imageSizes,
    className,
    contentClassName,
    bare,
    fill,
    children
  } = t0;
  const variant = t1 === undefined ? "compact" : t1;
  const progressive = t2 === undefined ? true : t2;
  const t = useTranslations("components.heroGridCard");
  let t3;
  if ($[0] !== t || $[1] !== title) {
    t3 = title ?? t("title");
    $[0] = t;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const resolvedTitle = t3;
  let t4;
  if ($[3] !== pictures.length || $[4] !== subtitle || $[5] !== t) {
    t4 = subtitle ?? t("imagesCount", {
      count: pictures.length
    });
    $[3] = pictures.length;
    $[4] = subtitle;
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const resolvedSubtitle = t4;
  let t5;
  if ($[7] !== morePhotosLabel || $[8] !== t) {
    t5 = morePhotosLabel ?? (count => t("morePhotos", {
      count
    }));
    $[7] = morePhotosLabel;
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const resolvedMorePhotosLabel = t5;
  let t6;
  if ($[10] !== resolvedSubtitle || $[11] !== resolvedTitle) {
    t6 = (resolvedTitle || resolvedSubtitle) && <div className="flex flex-col items-start gap-0.5">{resolvedTitle && <p className="text-lg font-semibold text-foreground-heading">{resolvedTitle}</p>}{resolvedSubtitle && <span className="text-xs text-muted-foreground">{resolvedSubtitle}</span>}</div>;
    $[10] = resolvedSubtitle;
    $[11] = resolvedTitle;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== addLabel || $[14] !== altPrefix || $[15] !== bare || $[16] !== fill || $[17] !== imagePriority || $[18] !== imageQuality || $[19] !== imageSizes || $[20] !== onAdd || $[21] !== onImagesClick || $[22] !== onTileClick || $[23] !== pictures || $[24] !== progressive || $[25] !== resolvedMorePhotosLabel || $[26] !== t || $[27] !== variant) {
    t7 = pictures.length > 0 ? <HeroImageGrid variant={variant} pictures={pictures} progressive={progressive} onClick={onImagesClick} onTileClick={onTileClick} altPrefix={altPrefix} imagePriority={imagePriority} imageQuality={imageQuality} imageSizes={imageSizes} data-testid="hero-image" morePhotosLabel={resolvedMorePhotosLabel} className={fill ? "lg:h-[328px] lg:aspect-auto" : undefined} /> : onAdd ? <button type="button" onClick={onAdd} data-testid="hero-image-empty" className={cn("flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-strong bg-muted text-faint-foreground transition-colors hover:bg-muted-strong", fill ? "lg:h-[328px]" : "aspect-[5/2]")}><ImagePlus className="h-8 w-8" /><span className="text-sm font-medium">{addLabel ?? t("addImages")}</span></button> : bare ? <div data-testid="hero-image-empty-grid" className={cn("grid grid-cols-[2fr_1fr] grid-rows-2 gap-1 w-full", fill ? "lg:h-[328px]" : "aspect-[5/2]")}><div className="row-span-2 flex items-center justify-center rounded-lg border border-border bg-muted"><ImageOff className="h-8 w-8 text-faint-foreground" /></div><div className="flex items-center justify-center rounded-lg border border-border bg-muted"><ImageOff className="h-6 w-6 text-faint-foreground" /></div><div className="flex items-center justify-center rounded-lg border border-border bg-muted"><ImageOff className="h-6 w-6 text-faint-foreground" /></div></div> : <div data-testid="hero-image-empty-readonly" className="aspect-[5/2] w-full"><NoImageAvailable className="rounded-lg" /></div>;
    $[13] = addLabel;
    $[14] = altPrefix;
    $[15] = bare;
    $[16] = fill;
    $[17] = imagePriority;
    $[18] = imageQuality;
    $[19] = imageSizes;
    $[20] = onAdd;
    $[21] = onImagesClick;
    $[22] = onTileClick;
    $[23] = pictures;
    $[24] = progressive;
    $[25] = resolvedMorePhotosLabel;
    $[26] = t;
    $[27] = variant;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== children || $[30] !== t6 || $[31] !== t7) {
    t8 = <>{t6}{t7}{children}</>;
    $[29] = children;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const inner = t8;
  if (bare) {
    let t9;
    if ($[33] !== contentClassName) {
      t9 = cn("flex flex-col gap-3", contentClassName);
      $[33] = contentClassName;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] !== inner || $[36] !== t9) {
      t10 = <div className={t9}>{inner}</div>;
      $[35] = inner;
      $[36] = t9;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    return t10;
  }
  let t9;
  if ($[38] !== className) {
    t9 = cn("overflow-hidden", className);
    $[38] = className;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== contentClassName) {
    t10 = cn("p-5 flex flex-col gap-3", contentClassName);
    $[40] = contentClassName;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== inner || $[43] !== t10) {
    t11 = <CardContent className={t10} data-sentry-element="CardContent" data-sentry-source-file="HeroGridCard.tsx">{inner}</CardContent>;
    $[42] = inner;
    $[43] = t10;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== t11 || $[46] !== t9) {
    t12 = <Card className={t9} data-sentry-element="Card" data-sentry-component="HeroGridCard" data-sentry-source-file="HeroGridCard.tsx">{t11}</Card>;
    $[45] = t11;
    $[46] = t9;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  return t12;
}